import { useState, useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { UserPlus, Search } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePmProperty } from '@/hooks/usePmProperty';
import { getPmUsers, listShiftTemplates } from '@/services/api.client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { QueryStateBoundary } from '@/components/ui/query-state-boundary';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InviteUserDialog } from '@/components/pm/InviteUserDialog';
import type { PmUser, PmUserStatus, ShiftTemplate } from '@/types';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { GuardDetailSheet } from './GuardDetailSheet';
import { RosterList } from './RosterList';
import { ShiftTemplateList } from './ShiftTemplateList';
import { AddRosterGuardDialog } from './AddRosterGuardDialog';
import { DevicesTab } from './DevicesTab';

type GuardsTab = 'guards' | 'templates' | 'roster' | 'devices';

const STATUS_COLOR: Record<PmUserStatus, string> = {
  active: 'var(--status-green)',
  suspended: 'var(--status-red)',
  pending: 'var(--status-amber)',
};

const DAYS: { label: string; dow: number }[] = [
  { label: 'Mon', dow: 1 },
  { label: 'Tue', dow: 2 },
  { label: 'Wed', dow: 3 },
  { label: 'Thu', dow: 4 },
  { label: 'Fri', dow: 5 },
  { label: 'Sat', dow: 6 },
  { label: 'Sun', dow: 0 },
];

function ScheduleGrid({
  templates,
  isLoading,
}: {
  templates: ShiftTemplate[];
  isLoading?: boolean;
}) {
  if (isLoading) {
    return <Skeleton className="h-20 w-full" />;
  }
  if (templates.length === 0) {
    return (
      <p className="text-xs text-[var(--text-tertiary)] py-4 text-center">No shift templates.</p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <div className="grid grid-cols-[minmax(140px,1fr)_repeat(7,minmax(28px,1fr))] gap-1 mb-1">
        <div /> {/* spacer for the template name column */}
        {DAYS.map(({ label }) => (
          <div
            key={label}
            className="text-center text-xs text-[var(--text-tertiary)] font-medium py-1"
          >
            {label}
          </div>
        ))}
      </div>
      <div className="space-y-1">
        {templates.map((tmpl) => (
          <div
            key={tmpl.id}
            className="grid grid-cols-[minmax(140px,1fr)_repeat(7,minmax(28px,1fr))] gap-1 items-center"
          >
            <div className="min-w-0 pr-2">
              <p
                className="text-xs font-medium text-[var(--text-primary)] truncate"
                title={tmpl.name}
              >
                {tmpl.name}
              </p>
              <p className="text-[10px] text-[var(--text-tertiary)]">
                {tmpl.startTime}–{tmpl.endTime}
              </p>
            </div>
            {DAYS.map(({ dow, label }) => {
              const active = tmpl.daysOfWeek.includes(dow);
              return (
                <div
                  key={dow}
                  title={
                    active
                      ? `${tmpl.name} on ${label}: ${tmpl.startTime}–${tmpl.endTime}`
                      : `Off on ${label}`
                  }
                  aria-label={active ? `Active on ${label}` : `Off on ${label}`}
                  className="rounded"
                  style={{
                    height: 24,
                    background: active ? 'var(--status-green)' : 'var(--border-subtle)',
                    opacity: active ? 1 : 0.4,
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export function GuardsPage() {
  const pm = usePmProperty();
  const propertyId = pm?.propertyId ?? '';
  const { user } = useAuth();
  const isPm =
    user?.propertyRoles.some((r) => r.propertyId === propertyId && r.role === 'property_manager') ??
    false;

  const [selectedGuardId, setSelectedGuardId] = useState<string | null>(null);
  const [tab, setTab] = useState<GuardsTab>('guards');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [rosterGuardOpen, setRosterGuardOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const guardsQuery = useQuery({
    queryKey: ['pm', 'users', propertyId, 'guard', page, debouncedSearch],
    queryFn: () =>
      getPmUsers(propertyId, {
        role: 'guard',
        page,
        perPage: 20,
        ...(debouncedSearch && { search: debouncedSearch }),
      }),
    enabled: !!propertyId,
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });

  const templatesQuery = useQuery({
    queryKey: ['shift-templates', propertyId],
    queryFn: () => listShiftTemplates(propertyId),
    enabled: !!propertyId,
  });

  const templates = templatesQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl text-[var(--text-primary)]">Guard Management</h1>
        {isPm && (
          <div className="flex gap-2">
            <Button
              size="sm"
              className="gap-1.5"
              style={{ background: 'var(--cta-primary-bg)', color: 'var(--text-inverse)' }}
              onClick={() => setRosterGuardOpen(true)}
            >
              <UserPlus className="h-4 w-4" />
              Add to Roster
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={() => setInviteOpen(true)}
            >
              <UserPlus className="h-4 w-4" />
              Invite (Login)
            </Button>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as GuardsTab)}>
        <TabsList>
          <TabsTrigger value="guards">Guards</TabsTrigger>
          <TabsTrigger value="templates">Shift Templates</TabsTrigger>
          <TabsTrigger value="roster">Roster</TabsTrigger>
          {/* Devices are restricted to property managers on the server (PM-only
              GET /devices). Security supervisors have no read access, so hide the
              tab entirely rather than let it fire a 403. */}
          {isPm && <TabsTrigger value="devices">Devices</TabsTrigger>}
        </TabsList>

        <TabsContent value="guards" className="mt-4">
          <div className="relative mb-3">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search guards by name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[55fr_45fr] gap-5">
            {/* Guards table */}
            <div className="rounded-xl border border-[var(--base\/faint-warm)] overflow-hidden">
              <div className="bg-[var(--bg-surface-raised)] px-4 py-2.5 border-b border-[var(--base\/faint-warm)]">
                <span className="text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wide">
                  Guards
                </span>
              </div>
              <QueryStateBoundary
                query={guardsQuery}
                isEmpty={(d) => (d?.data ?? []).length === 0}
                errorTitle="Couldn't load guards"
                emptyMessage="No guards assigned."
                loadingFallback={
                  <div className="p-4 space-y-3">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-10 w-full" />
                    ))}
                  </div>
                }
              >
                {(result) => (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[var(--base\/faint-warm)]">
                        {['Name', 'Status', 'Last Active'].map((h) => (
                          <th
                            key={h}
                            className="px-4 py-2.5 text-left text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wide"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.data.map((g: PmUser) => (
                        <tr
                          key={g.id}
                          className="border-b border-[var(--base\/faint-warm)] last:border-0 hover:bg-[var(--bg-surface-raised)] transition-colors cursor-pointer"
                          onClick={() => setSelectedGuardId(g.id)}
                        >
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-[var(--text-primary)]">
                              {g.fullName}
                            </p>
                            <p className="text-xs text-[var(--text-tertiary)]">{g.email}</p>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
                              style={{
                                color: STATUS_COLOR[g.status],
                                border: `1px solid ${STATUS_COLOR[g.status]}`,
                              }}
                            >
                              {g.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                            {g.lastActiveAt ? new Date(g.lastActiveAt).toLocaleString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </QueryStateBoundary>
              <PaginationControls
                currentPage={page}
                totalPages={guardsQuery.data?.meta?.totalPages ?? 1}
                onPageChange={setPage}
                disabled={guardsQuery.isPlaceholderData}
              />
            </div>

            {/* Shift schedule */}
            <div className="rounded-xl border border-[var(--base\/faint-warm)] overflow-hidden">
              <div className="bg-[var(--bg-surface-raised)] px-4 py-2.5 border-b border-[var(--base\/faint-warm)]">
                <span className="text-xs font-medium text-[var(--text-tertiary)] uppercase tracking-wide">
                  Shift Schedule · This Week
                </span>
              </div>
              <div className="p-4">
                <ScheduleGrid templates={templates} isLoading={templatesQuery.isLoading} />
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          <ShiftTemplateList propertyId={propertyId} isPm={isPm} />
        </TabsContent>

        <TabsContent value="roster" className="mt-4">
          <RosterList propertyId={propertyId} isPm={isPm} />
        </TabsContent>

        <TabsContent value="devices" className="mt-4">
          {isPm && <DevicesTab propertyId={propertyId} isPm={isPm} />}
        </TabsContent>
      </Tabs>

      {selectedGuardId && (
        <GuardDetailSheet
          propertyId={propertyId}
          guardId={selectedGuardId}
          isPm={isPm}
          onClose={() => setSelectedGuardId(null)}
        />
      )}

      {inviteOpen && (
        <InviteUserDialog
          open={inviteOpen}
          onOpenChange={(next) => setInviteOpen(next)}
          propertyId={propertyId}
          role="guard"
        />
      )}

      {rosterGuardOpen && (
        <AddRosterGuardDialog
          open={rosterGuardOpen}
          onOpenChange={(next) => setRosterGuardOpen(next)}
          propertyId={propertyId}
        />
      )}
    </div>
  );
}
